import { useLayoutEffect, useRef } from 'react'
import { useOrganization } from '@clerk/clerk-react'

import {
  createFactSuggestionSource,
  type FactSuggestionSource,
  type FactSuggestionStatus,
} from '@/components/milkdown/jinja/factSuggestions'
import { useFactCatalogueQuery } from './useFactCatalogueQuery'

const EMPTY_FACT_ROWS = [] as const

/**
 * Publishes the active org's Fact catalogue into the stable source the editor's
 * ProseMirror plugin reads, so React Query and Clerk stay out of the editor.
 * The source identity never changes, so loading Facts never recreates Crepe.
 */
export function useFactSuggestionSource(
  enabled: boolean
): FactSuggestionSource {
  const { organization } = useOrganization()
  const { data, isError, isFetching, isPending, refetch } =
    useFactCatalogueQuery(enabled)

  const sourceRef = useRef<FactSuggestionSource>(null)
  sourceRef.current ??= createFactSuggestionSource()
  const source = sourceRef.current

  useLayoutEffect(() => {
    if (!enabled) return

    const status: FactSuggestionStatus = !organization?.id
      ? 'ready'
      : !data && (isPending || isFetching)
        ? 'loading'
        : isError
          ? 'error'
          : 'ready'

    source.update(
      {
        organizationId: organization?.id ?? null,
        status,
        rows: data?.rows ?? EMPTY_FACT_ROWS,
      },
      refetch
    )
  }, [
    data,
    enabled,
    isError,
    isFetching,
    isPending,
    organization?.id,
    refetch,
    source,
  ])

  return source
}
